import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconMoonStars } from '@tabler/icons-react';
import { Button } from '@/components/Button';
import { ENV } from '@/lib/env';
import { useFeed } from '@/store/feed';

export default function DailyLimit() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const meta = useFeed(s => s.meta);
  const load = useFeed(s => s.load);

  // При прямом заходе на /limit (например после F5) store пуст — нужно дёрнуть
  // /api/v1/feed, чтобы получить актуальный limit_resets_at от бэка.
  useEffect(() => {
    if (!meta?.limit_resets_at && !meta?.next_card_in) {
      void load(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Предпочитаем абсолютный limit_resets_at — иначе таймер съезжает между mount'ами.
  // Fallback: next_card_in (если бэк не прислал абсолютную метку) или 12 часов.
  const resetsAtMs = meta?.limit_resets_at ? Date.parse(meta.limit_resets_at) : null;
  const initialSeconds = resetsAtMs !== null
    ? Math.max(0, Math.floor((resetsAtMs - Date.now()) / 1000))
    : (meta?.next_card_in ?? 12 * 3600);
  const [seconds, setSeconds] = useState(initialSeconds);

  useEffect(() => {
    setSeconds(
      resetsAtMs !== null
        ? Math.max(0, Math.floor((resetsAtMs - Date.now()) / 1000))
        : (meta?.next_card_in ?? 12 * 3600),
    );
  }, [resetsAtMs, meta?.next_card_in]);

  useEffect(() => {
    const id = setInterval(() => {
      if (resetsAtMs !== null) {
        setSeconds(Math.max(0, Math.floor((resetsAtMs - Date.now()) / 1000)));
      } else {
        setSeconds(s => Math.max(0, s - 1));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [resetsAtMs]);

  return (
    <div className="flex-1 flex flex-col bg-bg min-h-0">
      <main className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
        <div className="w-20 h-20 rounded-pill bg-dark text-text-inverse grid place-items-center">
          <IconMoonStars size={36} />
        </div>
        <h1 className="font-content text-[28px]">{t('feed.limit_reached_title')}</h1>
        <p className="text-text-muted max-w-sm">{t('feed.limit_reached_sub', { n: 30 })}</p>

        <div className="w-full max-w-sm bg-surface rounded-xl p-4 mt-2">
          <div className="text-xs text-text-muted">{t('feed.limit_reached_timer')}</div>
          <div className="font-content text-3xl mt-1">{fmt(seconds)}</div>
        </div>

        <button
          onClick={() => nav('/subscription')}
          className="w-full max-w-sm bg-dark text-text-inverse rounded-xl p-4 mt-1 text-left"
        >
          <div className="text-xs opacity-80">{t('feed.limit_unlock_eyebrow', { defaultValue: 'Снять ограничение' })}</div>
          <div className="text-base font-medium mt-0.5">{t('feed.limit_unlock', { p: 1 })}</div>
        </button>
      </main>

      <footer className="p-4 grid grid-cols-2 gap-3">
        <Button block variant="secondary" onClick={() => nav('/shelf')}>{t('feed.open_shelf')}</Button>
        <Button block onClick={() => window.Telegram?.WebApp?.openTelegramLink(`https://t.me/share/url?url=https://t.me/${ENV.BOT_USERNAME}`)}>
          {t('feed.invite_friend')}
        </Button>
      </footer>
    </div>
  );
}

function fmt(total: number) {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
function pad(n: number) { return n < 10 ? `0${n}` : `${n}`; }
